import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import styled from "styled-components";
import { toast } from "react-toastify";
import { apiClient } from "../../api/client";
import { useAppSelector } from "../../store/hooks";
import { checkRole } from "../recipe_page/recipe_page";

type Bpp = 1 | 2 | 4 | 8;
type PaletteName = "spectra6" | "bw" | "bwr" | "bwy" | "custom";
type Orientation = "portrait" | "landscape";

type PanelProfileRow = {
  _id: string;
  key: string;
  label: string;
  width: number;
  height: number;
  bpp: Bpp;
  palette: PaletteName;
  nativeOrientation: Orientation;
  bytesPerImage: number;
  notes?: string;
  createdAt?: string;
  updatedAt?: string;
};

type DeviceForCount = {
  deviceId: string;
  panelProfile?: string;
};

type EditDraft = {
  label: string;
  width: string;
  height: string;
  bpp: Bpp;
  palette: PaletteName;
  nativeOrientation: Orientation;
};

const BPP_OPTIONS: Bpp[] = [1, 2, 4, 8];
const PALETTE_OPTIONS: PaletteName[] = ["spectra6", "bw", "bwr", "bwy", "custom"];
const KEY_PATTERN = /^[a-z0-9]+(-[a-z0-9]+)*$/;

function extractErrorMessage(e: unknown): string {
  if (e && typeof e === "object") {
    const ax = e as { response?: { data?: { error?: string } }; message?: string };
    const fromBody = ax.response?.data?.error;
    if (typeof fromBody === "string" && fromBody) return fromBody;
    if (typeof ax.message === "string") return ax.message;
  }
  return "request failed";
}

function formatBytes(n?: number): string {
  if (n == null || Number.isNaN(n)) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function bytesPerImage(width: number, height: number, bpp: number): number {
  if (!Number.isFinite(width) || !Number.isFinite(height) || !Number.isFinite(bpp)) return 0;
  return Math.floor((width * height * bpp) / 8);
}

const Wrap = styled.div`
  min-height: calc(100vh - 64px);
  background: #f0f0f0;
  padding: 1.25rem;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New",
    monospace;
  @media (max-width: 700px) {
    padding: 12px 8px;
  }
`;

const Inner = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const Card = styled.div`
  background: #ffffff;
  border: 1px solid #232323;
  box-shadow: 6px 6px 0 #232323;
  padding: 1.25rem;
  @media (max-width: 700px) {
    padding: 1rem 0.85rem;
  }
`;

const BackLink = styled(Link)`
  align-self: flex-start;
  font-size: 0.85rem;
  font-weight: 700;
  color: #232323;
  text-decoration: underline;
  &:hover {
    color: #000;
  }
`;

const Title = styled.h1`
  margin: 0.5rem 0 0;
  font-size: 1.35rem;
  letter-spacing: -0.02em;
`;

const Subtitle = styled.p`
  margin: 0.25rem 0 0;
  font-size: 0.85rem;
  color: #444;
`;

const SectionTitle = styled.h2`
  margin: 0 0 0.75rem;
  font-size: 1rem;
  font-weight: 800;
`;

const TableWrap = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  @media (max-width: 700px) {
    margin: 0 -8px;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.8rem;
  th,
  td {
    border: 1px solid #232323;
    padding: 0.4rem 0.45rem;
    text-align: left;
    vertical-align: middle;
  }
  th {
    background: #fafafa;
    font-weight: 700;
  }
  tbody tr:nth-child(even) {
    background: #fafafa;
  }
  @media (max-width: 700px) {
    min-width: 720px;
  }
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 0.65rem;
  align-items: end;
`;

const FieldCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 0;
`;

const Label = styled.label`
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #555;
`;

const TextInput = styled.input`
  padding: 0.4rem 0.5rem;
  border: 1px solid #232323;
  font-family: inherit;
  font-size: 0.82rem;
  background: #fff;
  box-shadow: 2px 2px 0 #232323;
  box-sizing: border-box;
  width: 100%;
`;

const SmallInput = styled.input`
  padding: 0.3rem 0.4rem;
  border: 1px solid #232323;
  font-family: inherit;
  font-size: 0.78rem;
  background: #fff;
  box-sizing: border-box;
  width: 100%;
`;

const Select = styled.select`
  padding: 0.4rem 0.5rem;
  border: 1px solid #232323;
  font-family: inherit;
  font-size: 0.82rem;
  background: #fff;
  box-shadow: 2px 2px 0 #232323;
  box-sizing: border-box;
  width: 100%;
`;

const SmallSelect = styled.select`
  padding: 0.3rem 0.4rem;
  border: 1px solid #232323;
  font-family: inherit;
  font-size: 0.78rem;
  background: #fff;
  box-sizing: border-box;
  width: 100%;
`;

const ReadOnlyField = styled.div`
  padding: 0.4rem 0.5rem;
  border: 1px dashed #999;
  font-size: 0.82rem;
  color: #555;
  background: #fafafa;
  box-sizing: border-box;
`;

const PrimaryBtn = styled.button`
  padding: 0.45rem 0.85rem;
  border: 1px solid #232323;
  background: #ffd24a;
  color: #232323;
  font-family: inherit;
  font-weight: 800;
  font-size: 0.8rem;
  cursor: pointer;
  box-shadow: 3px 3px 0 #232323;
  &:hover:not(:disabled) {
    background: #ffdf73;
  }
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const SubtleBtn = styled.button`
  padding: 0.28rem 0.55rem;
  border: 1px solid #232323;
  background: #fff;
  font-family: inherit;
  font-weight: 700;
  font-size: 0.72rem;
  cursor: pointer;
  box-shadow: 2px 2px #232323;
  margin-right: 0.35rem;
  &:hover:not(:disabled) {
    background: #e8e8e8;
  }
`;

const HelpText = styled.p`
  margin: 0.75rem 0 0;
  font-size: 0.72rem;
  color: #666;
  line-height: 1.4;
`;

const Err = styled.p`
  color: crimson;
  margin: 0 0 0.75rem;
`;

const InlineErr = styled.p`
  margin: 0.5rem 0 0;
  font-size: 0.8rem;
  color: crimson;
`;

const FlashOk = styled.p`
  margin: 0.5rem 0 0;
  font-size: 0.8rem;
  color: #15803d;
  font-weight: 700;
`;

function toEditDraft(p: PanelProfileRow): EditDraft {
  return {
    label: p.label,
    width: String(p.width),
    height: String(p.height),
    bpp: p.bpp,
    palette: p.palette,
    nativeOrientation: p.nativeOrientation,
  };
}

export default function AotdIrlAdminDisplayTemplates() {
  const auth = useAppSelector((s) => s.auth);

  const [profiles, setProfiles] = useState<PanelProfileRow[]>([]);
  const [devices, setDevices] = useState<DeviceForCount[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState<EditDraft | null>(null);
  const [savingEdit, setSavingEdit] = useState(false);

  const [newKey, setNewKey] = useState("");
  const [newLabel, setNewLabel] = useState("");
  const [newWidth, setNewWidth] = useState("");
  const [newHeight, setNewHeight] = useState("");
  const [newBpp, setNewBpp] = useState<Bpp>(4);
  const [newPalette, setNewPalette] = useState<PaletteName>("spectra6");
  const [newOrientation, setNewOrientation] = useState<Orientation>("landscape");
  const [creating, setCreating] = useState(false);
  const [createErr, setCreateErr] = useState<string | null>(null);
  const [createOk, setCreateOk] = useState(false);

  const load = useCallback(async () => {
    try {
      const [profilesRes, devicesRes] = await Promise.all([
        apiClient.get<{ profiles?: PanelProfileRow[] }>("/api/aotd-irl/admin/panel-profiles"),
        apiClient.get<{ devices?: DeviceForCount[] }>("/api/aotd-irl/admin/devices"),
      ]);
      setProfiles(Array.isArray(profilesRes.data?.profiles) ? profilesRes.data.profiles : []);
      setDevices(Array.isArray(devicesRes.data?.devices) ? devicesRes.data.devices : []);
      setError(null);
    } catch (e: unknown) {
      setError(extractErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!createOk) return;
    const t = window.setTimeout(() => setCreateOk(false), 3000);
    return () => window.clearTimeout(t);
  }, [createOk]);

  const deviceCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const d of devices) {
      if (!d.panelProfile) continue;
      counts.set(d.panelProfile, (counts.get(d.panelProfile) ?? 0) + 1);
    }
    return counts;
  }, [devices]);

  const newBytesPerImage = useMemo(
    () => bytesPerImage(Number(newWidth), Number(newHeight), newBpp),
    [newWidth, newHeight, newBpp]
  );

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    const key = newKey.trim();
    const label = newLabel.trim();
    const width = Number(newWidth);
    const height = Number(newHeight);
    if (!key || !KEY_PATTERN.test(key)) {
      setCreateErr("key must be lowercase kebab-case, e.g. gooddisplay-gdep040e01-600x400");
      return;
    }
    if (!label) {
      setCreateErr("label is required");
      return;
    }
    if (!Number.isFinite(width) || width <= 0 || !Number.isFinite(height) || height <= 0) {
      setCreateErr("width and height must be positive numbers");
      return;
    }
    setCreating(true);
    setCreateErr(null);
    setCreateOk(false);
    try {
      const resp = await apiClient.post<{ profile?: PanelProfileRow }>(
        "/api/aotd-irl/admin/panel-profiles",
        {
          key,
          label,
          width: Math.floor(width),
          height: Math.floor(height),
          bpp: newBpp,
          palette: newPalette,
          nativeOrientation: newOrientation,
        }
      );
      if (resp.data?.profile) {
        setProfiles((prev) => [...prev, resp.data.profile as PanelProfileRow].sort((a, b) => a.key.localeCompare(b.key)));
      } else {
        await load();
      }
      setNewKey("");
      setNewLabel("");
      setNewWidth("");
      setNewHeight("");
      setNewBpp(4);
      setNewPalette("spectra6");
      setNewOrientation("landscape");
      setCreateOk(true);
      toast.success(`display template "${key}" created`, { position: toast.POSITION.BOTTOM_LEFT });
    } catch (err: unknown) {
      setCreateErr(extractErrorMessage(err));
    } finally {
      setCreating(false);
    }
  };

  const startEdit = (p: PanelProfileRow) => {
    setEditingKey(p.key);
    setEditDraft(toEditDraft(p));
  };

  const cancelEdit = () => {
    setEditingKey(null);
    setEditDraft(null);
  };

  const saveEdit = async (key: string) => {
    if (!editDraft) return;
    const width = Number(editDraft.width);
    const height = Number(editDraft.height);
    if (!editDraft.label.trim()) {
      toast.error("label is required", { position: toast.POSITION.BOTTOM_LEFT });
      return;
    }
    if (!Number.isFinite(width) || width <= 0 || !Number.isFinite(height) || height <= 0) {
      toast.error("width and height must be positive numbers", { position: toast.POSITION.BOTTOM_LEFT });
      return;
    }
    setSavingEdit(true);
    try {
      const resp = await apiClient.put<{ profile?: PanelProfileRow }>(
        `/api/aotd-irl/admin/panel-profiles/${encodeURIComponent(key)}`,
        {
          label: editDraft.label.trim(),
          width: Math.floor(width),
          height: Math.floor(height),
          bpp: editDraft.bpp,
          palette: editDraft.palette,
          nativeOrientation: editDraft.nativeOrientation,
        }
      );
      const updated = resp.data?.profile;
      if (updated) {
        setProfiles((prev) => prev.map((p) => (p.key === key ? updated : p)));
      } else {
        await load();
      }
      toast.success(`"${key}" updated`, { position: toast.POSITION.BOTTOM_LEFT });
      cancelEdit();
    } catch (err: unknown) {
      toast.error(extractErrorMessage(err), { position: toast.POSITION.BOTTOM_LEFT });
    } finally {
      setSavingEdit(false);
    }
  };

  if (auth.bootstrapStatus !== "ready") {
    return null;
  }
  if (!auth.token) {
    return <Navigate to="/login" replace />;
  }
  if (!checkRole(auth.role ?? "", "admin")) {
    return <Navigate to="/" replace />;
  }

  return (
    <Wrap>
      <Inner>
        <Card>
          <BackLink to="/aotd-irl/admin">← aotd-irl admin</BackLink>
          <Title>display templates</Title>
          <Subtitle>
            panel hardware profiles — width, height, colour depth and orientation used to seed a
            device's layout on enroll.
          </Subtitle>
        </Card>

        {loading && <Card>loading…</Card>}
        {error && <Err>{error}</Err>}

        {!loading && !error && (
          <Card>
            <SectionTitle>existing templates</SectionTitle>
            {profiles.length === 0 ? (
              <p style={{ margin: 0, fontSize: "0.85rem" }}>no display templates yet.</p>
            ) : (
              <TableWrap>
                <Table>
                  <thead>
                    <tr>
                      <th>key</th>
                      <th>label</th>
                      <th>size</th>
                      <th>orientation</th>
                      <th>bpp</th>
                      <th>palette</th>
                      <th>bytes / image</th>
                      <th>devices</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {profiles.map((p) => {
                      const isEditing = editingKey === p.key && editDraft;
                      const count = deviceCounts.get(p.key) ?? 0;
                      if (isEditing && editDraft) {
                        return (
                          <tr key={p.key}>
                            <td>
                              <code>{p.key}</code>
                            </td>
                            <td>
                              <SmallInput
                                value={editDraft.label}
                                onChange={(e) =>
                                  setEditDraft({ ...editDraft, label: e.target.value })
                                }
                              />
                            </td>
                            <td>
                              <div style={{ display: "flex", gap: "0.25rem", alignItems: "center" }}>
                                <SmallInput
                                  type="number"
                                  min={1}
                                  style={{ width: 70 }}
                                  value={editDraft.width}
                                  onChange={(e) =>
                                    setEditDraft({ ...editDraft, width: e.target.value })
                                  }
                                />
                                <span>×</span>
                                <SmallInput
                                  type="number"
                                  min={1}
                                  style={{ width: 70 }}
                                  value={editDraft.height}
                                  onChange={(e) =>
                                    setEditDraft({ ...editDraft, height: e.target.value })
                                  }
                                />
                              </div>
                            </td>
                            <td>
                              <SmallSelect
                                value={editDraft.nativeOrientation}
                                onChange={(e) =>
                                  setEditDraft({
                                    ...editDraft,
                                    nativeOrientation: e.target.value as Orientation,
                                  })
                                }
                              >
                                <option value="landscape">landscape</option>
                                <option value="portrait">portrait</option>
                              </SmallSelect>
                            </td>
                            <td>
                              <SmallSelect
                                value={editDraft.bpp}
                                onChange={(e) =>
                                  setEditDraft({ ...editDraft, bpp: Number(e.target.value) as Bpp })
                                }
                              >
                                {BPP_OPTIONS.map((b) => (
                                  <option key={b} value={b}>
                                    {b}
                                  </option>
                                ))}
                              </SmallSelect>
                            </td>
                            <td>
                              <SmallSelect
                                value={editDraft.palette}
                                onChange={(e) =>
                                  setEditDraft({
                                    ...editDraft,
                                    palette: e.target.value as PaletteName,
                                  })
                                }
                              >
                                {PALETTE_OPTIONS.map((pal) => (
                                  <option key={pal} value={pal}>
                                    {pal}
                                  </option>
                                ))}
                              </SmallSelect>
                            </td>
                            <td>
                              {formatBytes(
                                bytesPerImage(
                                  Number(editDraft.width),
                                  Number(editDraft.height),
                                  editDraft.bpp
                                )
                              )}
                            </td>
                            <td>{count}</td>
                            <td style={{ whiteSpace: "nowrap" }}>
                              <SubtleBtn
                                type="button"
                                disabled={savingEdit}
                                onClick={() => void saveEdit(p.key)}
                              >
                                Save
                              </SubtleBtn>
                              <SubtleBtn type="button" disabled={savingEdit} onClick={cancelEdit}>
                                Cancel
                              </SubtleBtn>
                            </td>
                          </tr>
                        );
                      }
                      return (
                        <tr key={p.key}>
                          <td>
                            <code>{p.key}</code>
                          </td>
                          <td>{p.label}</td>
                          <td>
                            {p.width} × {p.height}
                          </td>
                          <td>{p.nativeOrientation}</td>
                          <td>{p.bpp}</td>
                          <td>{p.palette}</td>
                          <td>{formatBytes(p.bytesPerImage)}</td>
                          <td>{count}</td>
                          <td style={{ whiteSpace: "nowrap" }}>
                            <SubtleBtn type="button" onClick={() => startEdit(p)}>
                              Edit
                            </SubtleBtn>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </TableWrap>
            )}
            <HelpText>
              editing here only changes the template's metadata (label, size, colour depth,
              orientation). to edit a template's default zone layout: open{" "}
              <Link to="/aotd-irl/admin/devices" style={{ color: "#232323", fontWeight: 700 }}>
                any device's layout editor
              </Link>
              , switch its panel-profile dropdown to match this key, then use "save preset" — there
              is no profile-only layout editor yet, so a device has to be open first.
            </HelpText>
          </Card>
        )}

        <Card>
          <SectionTitle>add display template</SectionTitle>
          <form onSubmit={handleCreate}>
            <FormGrid>
              <FieldCol>
                <Label htmlFor="dt-key">key</Label>
                <TextInput
                  id="dt-key"
                  value={newKey}
                  onChange={(e) => setNewKey(e.target.value)}
                  placeholder="gooddisplay-gdep040e01-600x400"
                  autoComplete="off"
                />
              </FieldCol>
              <FieldCol>
                <Label htmlFor="dt-label">label</Label>
                <TextInput
                  id="dt-label"
                  value={newLabel}
                  onChange={(e) => setNewLabel(e.target.value)}
                  placeholder='Good Display GDEP040E01 (4")'
                  autoComplete="off"
                />
              </FieldCol>
              <FieldCol>
                <Label htmlFor="dt-width">width (px)</Label>
                <TextInput
                  id="dt-width"
                  type="number"
                  min={1}
                  value={newWidth}
                  onChange={(e) => setNewWidth(e.target.value)}
                />
              </FieldCol>
              <FieldCol>
                <Label htmlFor="dt-height">height (px)</Label>
                <TextInput
                  id="dt-height"
                  type="number"
                  min={1}
                  value={newHeight}
                  onChange={(e) => setNewHeight(e.target.value)}
                />
              </FieldCol>
              <FieldCol>
                <Label htmlFor="dt-orientation">orientation</Label>
                <Select
                  id="dt-orientation"
                  value={newOrientation}
                  onChange={(e) => setNewOrientation(e.target.value as Orientation)}
                >
                  <option value="landscape">landscape</option>
                  <option value="portrait">portrait</option>
                </Select>
              </FieldCol>
              <FieldCol>
                <Label htmlFor="dt-bpp">bits per pixel</Label>
                <Select
                  id="dt-bpp"
                  value={newBpp}
                  onChange={(e) => setNewBpp(Number(e.target.value) as Bpp)}
                >
                  {BPP_OPTIONS.map((b) => (
                    <option key={b} value={b}>
                      {b}
                    </option>
                  ))}
                </Select>
              </FieldCol>
              <FieldCol>
                <Label htmlFor="dt-palette">palette</Label>
                <Select
                  id="dt-palette"
                  value={newPalette}
                  onChange={(e) => setNewPalette(e.target.value as PaletteName)}
                >
                  {PALETTE_OPTIONS.map((pal) => (
                    <option key={pal} value={pal}>
                      {pal}
                    </option>
                  ))}
                </Select>
              </FieldCol>
              <FieldCol>
                <Label>bytes / image</Label>
                <ReadOnlyField>{formatBytes(newBytesPerImage)}</ReadOnlyField>
              </FieldCol>
              <FieldCol>
                <PrimaryBtn type="submit" disabled={creating}>
                  {creating ? "creating…" : "add display template"}
                </PrimaryBtn>
              </FieldCol>
            </FormGrid>
          </form>
          <HelpText>
            bytes / image is computed server-side as floor(width × height × bpp / 8) and shown here
            for reference only — it isn't sent with the request.
          </HelpText>
          {createOk && <FlashOk>created</FlashOk>}
          {createErr && <InlineErr>{createErr}</InlineErr>}
        </Card>
      </Inner>
    </Wrap>
  );
}
